/**
 * The social door for school accounts (job #113, Tom 2026-09-24): a school
 * account sees neither Talk to humans nor Study groups in the mode tray, and
 * the router refuses it /talk, /connect, /games, /groups and /rooms.
 *
 * The client does NOT decide who a school account is. It asks
 * GET /api/social/door, which answers with the server's `isSchoolAccount` —
 * educational role, live school/class/group tags, class entity, demo row — so
 * there is one definition and it lives in api/_utils/socialEligibility.ts.
 * Nor is this the lock: every /api/social/* route refuses a school account on
 * its own. This only stops the app showing a door that would not open.
 *
 * FAILS OPEN, deliberately and only here: if the question cannot be answered
 * the rows show and the route opens, because the server still refuses a
 * school account, and an individual learner must never lose their rows to a
 * network blip. A failed answer is not remembered, so the next ask retries.
 */
import { computed, ref, watch, type ComputedRef } from 'vue'
import { resolveSupabase } from './schools/client'
import { socialHeld } from '@/config/releaseHolds'

/** null = not asked yet for this sign-in; the rows wait for an answer. */
export const schoolAccount = ref<boolean | null>(null)

let answeredFor: string | null = null
let inFlight: { userId: string; promise: Promise<boolean> } | null = null
// New requests supersede old responses; resets also invalidate pending session reads.
let generation = 0
let resetVersion = 0

/** Paths a school account is refused. Prefix match: /connect/:pairId too. */
export const SOCIAL_PATH_PREFIXES = ['/talk', '/connect', '/games', '/groups', '/rooms']

export function isSocialPath(path: string): boolean {
  return SOCIAL_PATH_PREFIXES.some((p) => path === p || path.startsWith(`${p}/`))
}

/** Forget the answer — sign-out, account switch, tests. */
export function resetSocialDoor(): void {
  generation++
  resetVersion++
  schoolAccount.value = null
  answeredFor = null
  inFlight = null
}

/**
 * Is the signed-in account a school account? Resolves false for nobody
 * signed in (the pages handle sign-in) and on any failure (see header).
 */
export async function checkSocialDoor(): Promise<boolean> {
  const sessionVersion = resetVersion
  const sb = resolveSupabase()
  let userId: string | null = null
  let token: string | null = null
  try {
    const { data } = (await sb?.auth.getSession()) ?? { data: null }
    userId = data?.session?.user?.id ?? null
    token = data?.session?.access_token ?? null
  } catch {
    /* no session to ask about */
  }
  if (sessionVersion !== resetVersion) return false
  if (!userId || !token) {
    resetSocialDoor()
    return false
  }
  if (answeredFor === userId && schoolAccount.value !== null) return schoolAccount.value
  if (inFlight?.userId === userId) return inFlight.promise

  const mine = ++generation
  const promise = (async () => {
    try {
      const resp = await fetch('/api/social/door', { headers: { Authorization: `Bearer ${token}` } })
      const data = await resp.json().catch(() => ({}))
      if (mine !== generation) return false
      if (resp.ok && typeof data.schoolAccount === 'boolean') {
        schoolAccount.value = data.schoolAccount
        answeredFor = userId
        return data.schoolAccount as boolean
      }
    } catch {
      /* fall through: fail open */
    }
    if (mine !== generation) return false
    schoolAccount.value = false
    answeredFor = null
    return false
  })()
  inFlight = { userId, promise }
  void promise.finally(() => { if (inFlight?.promise === promise) inFlight = null })
  return promise
}

/**
 * The gate both tray parents put on Talk to humans and Study groups: a
 * signed-in, non-guest learner whose account the server says is not a school
 * account. Asks on sign-in, and again whenever the sign-in changes.
 */
export function useSocialRowsGate(signedIn: ComputedRef<boolean>): ComputedRef<boolean> {
  // Release hold (config/releaseHolds.ts): no rows, and no question to the server.
  if (socialHeld()) return computed(() => false)
  watch(signedIn, (on) => { if (on) void checkSocialDoor(); else resetSocialDoor() }, { immediate: true })
  return computed(() => signedIn.value && schoolAccount.value === false)
}
